import * as React from "react";
import { observer } from "mobx-react";
import { observable } from "mobx";
import { HTMLTable } from "@blueprintjs/core";
import { LookupStore } from "../../stores/LookupStore";
import { SpedApuracaoStore } from "../../stores/sped/SpedApuracaoStore";
import { MDBBtn, MDBCol, MDBDropdown, MDBDropdownItem, MDBDropdownMenu, MDBDropdownToggle, MDBRow } from "mdbreact";
import { IconButton } from "@mui/material";
import { newIReceita } from "../../model";
import moment from "moment";
import { get15thDayOfNextMonth } from "../../utils";
import { DaeAvulsoStore } from "../../stores/dae-avulso/DaeAvulsoStore";

const numeral = require("numeral");

interface Props {
  item: any
  onClose: () => void;
}

@observer
export default class ApuracaoSaidaTable extends React.Component<Props, {}> {

  obs = observable({
    apuracao: [] as any,
    impostos: [] as any,
    imposto_selecionado: "" as string,
    id_classificacao_imposto: "" as string,
  })

  constructor(props: any) {
    super(props);
  }

  /**
 * Carrega as classificações de ICMS e prepara os impostos para exibição.
 * 
 * A função realiza os seguintes passos:
 * 1. Carrega as classificações de ICMS para a UF da empresa selecionada usando a função `loadIcmsClassificacoes` de `LookupStore`.
 * 2. Para cada imposto presente no item, busca sua classificação ICMS através da função `getIcmsClassificacao` e atualiza os campos `name`, `codigo_receita` e `dae_avulso` com os dados da classificação.
 * 
 * @returns {Promise<void>} Retorna uma promise que resolve após a conclusão do carregamento e atualização dos impostos.
 */
  async componentDidMount() {

    await LookupStore.loadIcmsClassificacoes(SpedApuracaoStore.obs.selectedEmpresaSaida.uf);

    this.obs.impostos = this.props.item.impostos?.map(imp => {

      const cla = LookupStore.getIcmsClassificacao(imp.id_classificacao_imposto);

      imp.name = cla.nome
      imp.codigo_receita = cla.codigo_receita
      imp.dae_avulso = cla.dae_avulso

      return imp

    })

  }

  onTreeStateChange = (state) => { }

  /**
 * Retorna um conjunto de espaços em branco (usando a entidade HTML `&nbsp;`) para adicionar espaçamento visual.
 * 
 * A função é usada para adicionar espaçamento ou margens entre itens na interface de usuário.
 * 
 * @returns {JSX.Element} Um fragmento JSX contendo espaços não quebráveis.
 */
  marginItem() {
    return (<>&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;</>)
  }

  /**
 * Retorna um conjunto maior de espaços em branco (usando a entidade HTML `&nbsp;`) para adicionar mais espaçamento visual.
 * 
 * A função é usada para adicionar margens ou espaçamentos maiores entre subitens na interface de usuário.
 * 
 * @returns {JSX.Element} Um fragmento JSX contendo espaços não quebráveis.
 */
  marginSubitem() {
    return (<>&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;</>)
  }

  /**
 * Retorna uma linha vazia de tabela (`<tr>`) com células vazias, usada para criar espaçamento visual em tabelas.
 * 
 * A função cria uma linha de tabela com células vazias e um caractere de espaço invisível em uma das células.
 * 
 * @returns {JSX.Element} Uma linha de tabela contendo células vazias e um espaço invisível.
 */
  rowEmpty() {

    return (<tr>
      <td>{"\u200B"}</td>
      <td></td>
      <td></td>
      <td></td>
    </tr>)

  }

  /**
 * Inicia o processo de exportação da apuração de saída (SPED) para a empresa selecionada.
 * 
 * A função:
 * 1. Monta o objeto `apuracoes` com as informações da empresa selecionada, período de apuração,
 *    classificação de imposto e tipo de exportação.
 * 2. Chama a função `export` do `SpedApuracaoStore` para enviar a solicitação de exportação ao backend.
 * 3. Reseta a paginação e a contagem de tarefas de relatório.
 * 4. Busca a primeira página de tarefas de relatório usando `handleTasksPaged`.
 * 5. Alterna a aba selecionada para `"relatorio"`, para exibir os resultados.
 * 
 * Esse fluxo garante que, após solicitar a exportação, o usuário já veja os relatórios
 * relacionados à apuração de saída.
 * 
 * @async
 * @returns {Promise<void>} Retorna uma Promise resolvida quando todas as operações forem concluídas.
 */

  handleExportApuracaoSaida = async () => {
  
    let apuracoes = {
      cnpj: SpedApuracaoStore.obs.selectedEmpresaSaida.cnpj,
      razao_social: SpedApuracaoStore.obs.selectedEmpresaSaida.razao_social,
      uf: SpedApuracaoStore.obs.selectedEmpresaSaida.uf,
      date_start: SpedApuracaoStore.obs.periodoSaida[0],
      date_end: SpedApuracaoStore.obs.periodoSaida[1],
      id_classificacao_imposto : this.obs.id_classificacao_imposto,
      tipo_exportacao: "sped_apuracao_saida",
    }

    await SpedApuracaoStore.export(apuracoes);

    SpedApuracaoStore.obs.page_tasks_relatorio = 1
    SpedApuracaoStore.obs.length_tasks_relatorio = 0

    await SpedApuracaoStore.handleTasksPaged(1);

    SpedApuracaoStore.obs.selectedTab = "relatorio";

  };

  /**
 * Função que lida com o clique em um nome de imposto, preparando as informações detalhadas da apuração.
 * 
 * A função coleta e organiza os dados da apuração para exibição. Ela gera uma série de linhas de tabela (`<tr>`) com os dados calculados, como saldo credor, débitos, créditos, estornos, e outros valores relacionados.
 * 
 * @param {Object} nodeData Dados do nó selecionado, contendo as informações de débitos, créditos, e saldos para exibição.
 * 
 * @returns {void} Não retorna nenhum valor. A função apenas gera as linhas de apuração e atualiza o estado.
 */
  onNameClick = (nodeData) => {

    this.obs.apuracao = []
    this.obs.imposto_selecionado = ""
    this.obs.id_classificacao_imposto = nodeData.id_classificacao_imposto

    this.obs.imposto_selecionado = nodeData.name

    const rows = []

    rows.push(<tr>
      <td>Saldo credor anterior</td>
      <td></td>
      <td></td>
      <td className="valor-table-apuracao">{numeral(nodeData.saldo_anterior || 0).format("0,0.00")}</td>
    </tr>)

    rows.push(this.rowEmpty())

    rows.push(<tr>
      <td>Total de débitos</td>
      <td></td>
      <td></td>
      <td className="valor-table-apuracao">{numeral(nodeData.sum_debitos || 0).format("0,0.00")}</td>
    </tr>)

    rows.push(<tr>
      <td>{this.marginItem()}Débitos pelas saídas</td>
      <td></td>
      <td className="valor-table-apuracao">{numeral(nodeData.debitos_pelas_saidas || 0).format("0,0.00")}</td>
      <td></td>
    </tr>)

    rows.push(<tr>
      <td>{this.marginItem()}Outros Débitos</td>
      <td></td>
      <td className="valor-table-apuracao">{numeral(nodeData.sum_outros_debitos || 0).format("0,0.00")}</td>
      <td></td>
    </tr>)

    nodeData?.outros_debitos?.forEach(value => {

      rows.push(<tr>
        <td>{this.marginSubitem()}{value.tabela_sped_codigo}</td>
        <td className="valor-table-apuracao">{numeral(value.valor || 0).format("0,0.00")}</td>
        <td></td>
        <td></td>
      </tr>)

    })

    rows.push(<tr>
      <td>{this.marginItem()}Estorno de créditos</td>
      <td></td>
      <td className="valor-table-apuracao">{numeral(nodeData.sum_estorno_creditos).format("0,0.00")}</td>
      <td></td>
    </tr>)

    nodeData?.estorno_creditos?.forEach(value => {

      rows.push(<tr>
        <td>{this.marginSubitem()}{value.tabela_sped_codigo}</td>
        <td className="valor-table-apuracao">{numeral(value.valor || 0).format("0,0.00")}</td>
        <td></td>
        <td></td>
      </tr>)

    })

    rows.push(this.rowEmpty())

    rows.push(<tr>
      <td>Total de créditos</td>
      <td></td>
      <td></td>
      <td className="valor-table-apuracao">{numeral(nodeData.sum_creditos || 0).format("0,0.00")}</td>
    </tr>)

    rows.push(<tr>
      <td>{this.marginItem()}Créditos pelas entradas</td>
      <td></td>
      <td className="valor-table-apuracao">{numeral(nodeData.creditos_pelas_entradas || 0).format("0,0.00")}</td>
      <td></td>
    </tr>)

    rows.push(<tr>
      <td>{this.marginItem()}Outros créditos</td>
      <td></td>
      <td className="valor-table-apuracao">{numeral(nodeData.sum_outros_creditos || 0).format("0,0.00")}</td>
      <td></td>
    </tr>)

    nodeData?.outros_creditos?.forEach(value => {

      rows.push(<tr>
        <td>{this.marginSubitem()}{value.tabela_sped_codigo}</td>
        <td className="valor-table-apuracao">{numeral(value.valor || 0).format("0,0.00")}</td>
        <td></td>
        <td></td>
      </tr>)

    })

    rows.push(<tr>
      <td>{this.marginItem()}Crédito presumido</td>
      <td></td>
      <td className="valor-table-apuracao">{numeral(nodeData.sum_presumido_valores_credito).format("0,0.00")}</td>
      <td></td>
    </tr>)

    nodeData?.presumido_valores_credito?.forEach(value => {

      rows.push(<tr>
        <td>{this.marginSubitem()}{value.nome} - {value.tabela_sped_codigo}</td>
        <td className="valor-table-apuracao">{numeral(value.valor || 0).format("0,0.00")}</td>
        <td></td>
        <td></td>
      </tr>)

    })

    rows.push(<tr>
      <td>{this.marginItem()}Estorno de débitos</td>
      <td></td>
      <td className="valor-table-apuracao">{numeral(nodeData.sum_estorno_debitos).format("0,0.00")}</td>
      <td></td>
    </tr>)

    nodeData?.estorno_debitos?.forEach(value => {

      rows.push(<tr>
        <td>{this.marginSubitem()}{value.tabela_sped_codigo}</td>
        <td className="valor-table-apuracao">{numeral(value.valor || 0).format("0,0.00")}</td>
        <td></td>
        <td></td>
      </tr>)

    })

    rows.push(this.rowEmpty())

    rows.push(<tr>
      <td>Outros acréscimos</td>
      <td></td>
      <td></td>
      <td className="valor-table-apuracao">{numeral(nodeData.sum_outros_acrescimos).format("0,0.00")}</td>
    </tr>)

    rows.push(<tr>
      <td>{this.marginItem()}Acréscimos</td>
      <td></td>
      <td className="valor-table-apuracao">{numeral(nodeData.sum_outros_acrescimos).format("0,0.00")}</td>
      <td></td>
    </tr>)

    nodeData?.outros_acrescimos?.forEach(value => {

      rows.push(<tr>
        <td>{this.marginSubitem()}{value.tabela_sped_codigo}</td>
        <td className="valor-table-apuracao">{numeral(value.valor || 0).format("0,0.00")}</td>
        <td></td>
        <td></td>
      </tr>)

    })

    rows.push(this.rowEmpty())

    rows.push(<tr>
      <td>Outros deduções</td>
      <td></td>
      <td></td>
      <td className="valor-table-apuracao">{numeral(nodeData.sum_outros_deducoes).format("0,0.00")}</td>
    </tr>)

    rows.push(<tr>
      <td>{this.marginItem()}Deduções</td>
      <td></td>
      <td className="valor-table-apuracao">{numeral(nodeData.sum_outros_deducoes).format("0,0.00")}</td>
      <td></td>
    </tr>)

    nodeData?.outros_deducoes?.forEach(value => {

      rows.push(<tr>
        <td>{this.marginSubitem()}{value.tabela_sped_codigo}</td>
        <td className="valor-table-apuracao">{numeral(value.valor || 0).format("0,0.00")}</td>
        <td></td>
        <td></td>
      </tr>)

    })

    rows.push(this.rowEmpty())
    rows.push(this.rowEmpty())

    rows.push(<tr>
      <td><b>{(nodeData.saldo || 0) < 0 ? "ICMS a recolher" : "Saldo credor de ICMS para o mês seguinte"}</b></td>
      <td></td>
      <td></td>
      <td className="valor-table-apuracao"><b>{numeral(Math.abs(nodeData.saldo || 0)).format("0,0.00")}</b></td>
    </tr>)

    this.obs.apuracao = rows

  }

  /**
 * Cria e configura os dados necessários para gerar um DAE (Documento de Arrecadação de Receitas).
 * 
 * A função configura os valores de vencimento, cálculo, código de receita, valor a pagar, data de referência e CNPJ da empresa. 
 * Em seguida, abre o formulário do DAE avulso.
 * 
 * @param {Object} value Dados do imposto e saldo, usados para calcular o valor a ser pago no DAE.
 * 
 * @returns {void} Não retorna nenhum valor. A função apenas modifica o estado da aplicação para abrir o formulário do DAE avulso.
 */
  handleDae(value) {

    DaeAvulsoStore.obs.receita = newIReceita()

    DaeAvulsoStore.obs.date_vencimento = get15thDayOfNextMonth(moment(SpedApuracaoStore.obs.periodoSaida[0]).toDate())
    DaeAvulsoStore.obs.date_calculo = new Date()

    DaeAvulsoStore.obs.receita.codigo_receita = value.codigo_receita;
    DaeAvulsoStore.obs.receita.valor_pagar = Math.abs(value.saldo || 0);

    DaeAvulsoStore.obs.receita.data_referencia = moment(SpedApuracaoStore.obs.periodoSaida[0]).format("MM/YYYY");

    DaeAvulsoStore.obs.receita.cnpj = SpedApuracaoStore.obs.selectedEmpresaSaida.cnpj

    DaeAvulsoStore.obs.open_dae_avulso = true

  }

  /**
 * Verifica se o DAE (Documento de Arrecadação de Receitas) pode ser gerado.
 * 
 * A função realiza a verificação do saldo total (débito - crédito) e, caso o saldo seja negativo, 
 * exibe um botão de geração de DAE, se o DAE avulso for permitido e a empresa não for do Simples Nacional.
 * 
 * @param {Object} value Dados do imposto e saldo, usados para determinar a necessidade de geração do DAE.
 * 
 * @returns {JSX.Element} Um botão de ícone para gerar o DAE ou um botão desabilitado, dependendo da condição.
 */
  valid_dae(value) {

    const saldo_total = ((value.saldo_anterior || 0) + (value.sum_creditos || 0)) - (value.sum_debitos || 0)

    return (

      <>

        {saldo_total < 0 && value.dae_avulso && SpedApuracaoStore.obs.selectedEmpresaSaida.uf == "PA" && !SpedApuracaoStore.obs.selectedEmpresaSaida.simples_nacional ?

          <IconButton className="icon-dae">
            <img src="/images/arquivo.png" width={50} onClick={() => this.handleDae(value)} />
          </IconButton>

          :

          <IconButton disabled style={{ pointerEvents: "none" }}>{"\u200B"}</IconButton>

        }

      </>

    )

  }

  render() {

    return (
      <>
        <MDBRow>
          <MDBCol md="4" style={{ overflow: "auto", borderRight: "1px solid rgba(27, 99, 111, 0.15)" }}>
            {this.obs.impostos && this.obs.impostos.length ?
              <>
                <div style={{ fontSize: 14, marginBottom: 10 }}><b>IMPOSTOS</b></div>

                {this.obs.impostos.map(value => {

                  return (

                    <MDBBtn className={this.obs.imposto_selecionado == value.name ? "list-impostos list-impostos-selected" : "list-impostos"} onClick={() => this.onNameClick(value)}>

                      <div style={{ width: "100%", display: "flex", justifyContent: "space-between", alignItems: "center" }}>

                        <span>{value.name}</span>

                        {this.valid_dae(value)}

                      </div>

                    </MDBBtn>)

                })}

              </>

              :

              <div style={{ fontSize: 14, marginBottom: 10 }}>
                <b>Nenhum imposto encontrado!</b>
              </div>
            }

          </MDBCol>

          <MDBCol md="8">

            {Object.keys(this.obs.apuracao).length ?

              <>

                <MDBCol md="12" className="buttonExcel">
                  <MDBDropdown>
                    <MDBDropdownToggle caret className="btnDefault btnExportResultados">

                      Com os Resultados
                    </MDBDropdownToggle>
                    <MDBDropdownMenu basic>

                      <MDBDropdownItem
                        onClick={() => this.handleExportApuracaoSaida()}
                        className="actionMani"
                      >
                        <img src="/images/download.svg" width={18} className="iconsActionMani" />{" "}
                        Exportar Excel
                      </MDBDropdownItem>

                    </MDBDropdownMenu>
                  </MDBDropdown>
                </MDBCol>
                
                <div style={{ fontSize: 14, textAlign: "center", marginBottom: 10 }}>
                  <b>IMPOSTO: {this.obs.imposto_selecionado}</b>
                </div>

                <HTMLTable className="table-hover " style={{ width: "100%" }} small striped>
                  <thead>
                    <tr>
                      <th style={{ borderRight: "1px solid rgba(27, 99, 111, 0.15)", width: "50%", textAlign: "center" }}>Descrição</th>
                      <th style={{ borderRight: "1px solid rgba(27, 99, 111, 0.15)", width: "15%", textAlign: "center" }}>Valor</th>
                      <th style={{ borderRight: "1px solid rgba(27, 99, 111, 0.15)", width: "15%", textAlign: "center" }}>Subtotal</th>
                      <th style={{ width: "20%", textAlign: "center" }}>Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {this.obs.apuracao}
                  </tbody>
                </HTMLTable>

              </>

              : <></>}

          </MDBCol>
        </MDBRow>

      </>
    )
  }
}